import { format } from 'date-fns';
import { InsuranceSubscriberRelationship } from 'domains/client';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';
import {
  EvaluationFormType,
  paymentMethodIsInsurance,
  PrimaryInsuranceSubscriberType,
} from 'domains/signupDirect/schemas/evaluationSchema';
import { checkEligibility, EligibilityCheckPatientParams } from 'domains/signupv4/Insurance/queries';
import { UseFormSetValue } from 'react-hook-form';
import { autoPCPSchema, PCPInputMode } from './PCP/pcpSchemas';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { signupCanBookEvaluation } from 'domains/signupDirect/businessLogic/canBookEvaluation';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useTrack } from 'utils/hooks/useMixPanel';

type SubmitParams = {
  data: EvaluationFormType;
  setValue: UseFormSetValue<EvaluationFormType>;
  trackFn: ReturnType<typeof useTrack>['track'];
};

/** For eligibility ONLY */
const getEligibilityCheckSubscriberDetails = (data: EvaluationFormType): Required<EligibilityCheckPatientParams> => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  const result: EligibilityCheckPatientParams = {
    memberID: data.billingInfo.memberId,
  };

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    result.dateOfBirth = format(data.billingInfo.subscriber.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.billingInfo.subscriber.firstName;
    result.lastName = data.billingInfo.subscriber.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[data.billingInfo.subscriber.relationShipToClient];
  } else {
    result.dateOfBirth = format(data.clientInfo.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.clientInfo.firstName;
    result.lastName = data.clientInfo.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[InsuranceSubscriberRelationship.Self];
  }

  return result as Required<EligibilityCheckPatientParams>;
};

/** For Salesforce leads */
export const getInsurancePrimarySubscriberDetails = (data: EvaluationFormType): PrimaryInsuranceSubscriberType => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    return data.billingInfo.subscriber as PrimaryInsuranceSubscriberType;
  } else {
    return {
      dateOfBirth: data.clientInfo.dateOfBirth,
      firstName: data.clientInfo.firstName,
      lastName: data.clientInfo.lastName,
      relationShipToClient: InsuranceSubscriberRelationship.Self,
    };
  }
};

export const handleCheckEligibility = async ({ data, setValue, trackFn }: SubmitParams) => {
  setValue('billingInfo.eligibilityCheckResult', undefined);
  setValue('primaryPhysician.mode', PCPInputMode.disabled);

  if (!paymentMethodIsInsurance(data.billingInfo)) throw 'Expected billingInfo.type to be insurance';

  const { insuranceData } = data.billingInfo;

  const checkResult = await checkEligibility({
    checkParams: {
      payerID: insuranceData.payerId,
      state: insuranceData.state,
      npi: insuranceData.npiOverride?.toString(),
      payerIDOverride: insuranceData.eligibilityPayerIdOverride?.toString(),
      subscriber: getEligibilityCheckSubscriberDetails(data),
    }, //@TODO(juan) REFACTOR
    insuranceAllowSelfSignUp: insuranceData.allowSelfSignUp,
    insuranceInNetwork: insuranceData.networkStatus,
  });

  // Set the eligibility check result (status)
  setValue('billingInfo.eligibilityCheckResult', checkResult.status);

  if (checkResult.status !== EligibilityCheckResult.IN_NETWORK) {
    return;
  }

  /** note(juan): We only want to trigger PCP section IF eligibility is ok to proceed and PCP is required */
  const canBookEval = signupCanBookEvaluation({
    billingInfoType: data.billingInfo.type,
    eligibilityResult:
      'eligibilityCheckResult' in data.billingInfo ? data.billingInfo.eligibilityCheckResult : undefined,
    hasEnteredCCInfo: true,
  });

  const isPCPRequired = data.primaryPhysician.isPCPRequired;
  const shouldAskForPCP = canBookEval && isPCPRequired;

  if (shouldAskForPCP) {
    setValue('primaryPhysician.mode', PCPInputMode.Auto);

    if (checkResult.pcp) {
      const { pcp } = checkResult;

      const fullName = [pcp.firstName, pcp.middleName, pcp.name].filter(Boolean).join(' ');

      const parsedPCP = autoPCPSchema.safeParse({
        name: fullName,
        phone: pcp.phone,
        officeName: pcp.address ? `${pcp.address.address1}, ${pcp.address.city}, ${pcp.address.state}` : undefined,
        npiNumber: pcp.npi,
      });

      if (parsedPCP.success) {
        setValue('primaryPhysician.fullName', parsedPCP.data.fullName);
        setValue('primaryPhysician.phone', parsedPCP.data.phone);
        setValue('primaryPhysician.office', parsedPCP.data.office);
        setValue('primaryPhysician.npiNumber', parsedPCP.data.npiNumber);

        trackFn(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.PCP.PCP_PREFILLED);
      }
    }
  }

  return checkResult.status;
};
